import React, { useEffect, useMemo, useRef, useState } from "react";
import { BotResponse, sendRequest } from "../../../services/restAPI/endpoints/Placefinder";
import "./Placefinder.less";
import { Button, Message } from "../../BP2Components";
import { useIntl } from "react-intl";
import { v4 as uuidv4 } from "uuid";
import clsx from "clsx";
import { ChatMessage, PlaceFinderProps, ProgressStep, ProgressUI } from "./types";

const CHAT_HISTORY_KEY = "placefinder_chat_history";

const clampNumber = (value: number, minValue: number, maxValue: number) =>
   Math.max(minValue, Math.min(maxValue, value));

const normalizeStatus = (res): number => {
   if (res === null) return 200;
   if (typeof res === "number") return res;
   if (typeof res?.status === "number") return res.status;
   return 200;
};

const isOkStatus = (status: number) => status >= 200 && status < 300;

const Placefinder = ({
   refreshList,
   radius,
   centerLocation,
   isPlaceFinderChatOpen,
   onJobRunningChange,
}: PlaceFinderProps) => {
   const intl = useIntl();
   const createMessageId = () => uuidv4();

   const PROGRESS_FLOW: Array<{
      key: Exclude<ProgressStep, "done" | "error">;
      label: string;
   }> = [
      { key: "start", label: intl.formatMessage({ id: "placefinder.start" }) },
      { key: "queued", label: intl.formatMessage({ id: "placefinder.queue" }) },
      { key: "collecting", label: intl.formatMessage({ id: "placefinder.collect" }) },
      { key: "selecting", label: intl.formatMessage({ id: "placefinder.choice" }) },
      { key: "refreshing", label: intl.formatMessage({ id: "placefinder.refresh" }) },
   ];

   const STEP_BADGE_LABEL: Record<ProgressStep, string> = {
      start: intl.formatMessage({ id: "placefinder.start" }),
      queued: intl.formatMessage({ id: "placefinder.queue" }),
      collecting: intl.formatMessage({ id: "placefinder.collect" }),
      selecting: intl.formatMessage({ id: "placefinder.choice" }),
      refreshing: intl.formatMessage({ id: "placefinder.refresh" }),
      done: "",
      error: intl.formatMessage({ id: "placefinder.error" }),
   };

   const initialBotMessage = intl.formatMessage({ id: "placefinder.initialBotMessage" });
   const botResponseMessage = intl.formatMessage({ id: "placefinder.botResponseMessage" });

   const normalizeChatHistory = (raw) => {
      if (!Array.isArray(raw)) return [];

      return raw
         .filter((message) => message && typeof message === "object")
         .filter((message) => !message.isErrorCard)
         .filter((message) => !message.hasWarnings)
         .map((message) => ({
            id:
               typeof message.id === "string" && message.id.length ? message.id : createMessageId(),
            sender: message.sender === "user" || message.sender === "bot" ? message.sender : "bot",
            text: typeof message.text === "string" ? message.text : "",
            isInitial: Boolean(message.isInitial),
            hasWarnings: Boolean(message.hasWarnings),
            isFinal: Boolean(message.isFinal),
            meta:
               message.meta && typeof message.meta === "object"
                  ? {
                       radiusLabel:
                          typeof message.meta.radiusLabel === "string"
                             ? message.meta.radiusLabel
                             : undefined,
                    }
                  : undefined,
         }))
         .filter((message) => message.text.length > 0);
   };

   const [chatHistory, setChatHistory] = useState<ChatMessage[]>(() => {
      const saved = localStorage.getItem(CHAT_HISTORY_KEY);
      if (saved) {
         try {
            const parsed = JSON.parse(saved);
            const normalized = normalizeChatHistory(parsed);
            if (normalized.length) {
               return normalized;
            }
         } catch (error) {
            console.error(error);
         }
      }
      return [{ id: createMessageId(), sender: "bot", text: initialBotMessage, isInitial: true }];
   });

   const [input, setInput] = useState<string>("");
   const [isBotResponseLoading, setIsBotResponseLoading] = useState<boolean>(false);

   const chatHistoryRef = useRef<HTMLDivElement>(null);

   const jobCreatedRef = useRef(false);
   const refreshedInitialDataRef = useRef(false);
   const refreshedFinalDataRef = useRef(false);
   const lastPolledStatusRef = useRef<BotResponse["status"] | null>(null);

   const displayedProgressRef = useRef<number | undefined>(undefined);
   const completedStepIndexRef = useRef<number>(-1);

   // refresh list
   const refreshCompletedRef = useRef(false);
   const refreshStatusRef = useRef<number | null>(null);
   const pendingFinalMessageRef = useRef<ChatMessage | null>(null);
   const refreshStartedRef = useRef(false);

   const [progressUI, setProgressUI] = useState<ProgressUI>({
      visible: false,
      step: "start",
      title: "",
      subtitle: "",
      progress: undefined,
      initialResultsToast: false,
   });

   const scrollToBottom = () => {
      if (chatHistoryRef.current) {
         chatHistoryRef.current.style.scrollBehavior = "smooth";
         chatHistoryRef.current.scrollTop = chatHistoryRef.current.scrollHeight;
      }
   };

   const radiusLabel = useMemo(() => {
      if (Number.isNaN(radius)) return "";
      if (radius < 1000) return `${Math.round(radius)} m`;
      return `${(radius / 1000).toFixed(1)} km`;
   }, [radius]);

   const computeSmoothedProgress = (poll: BotResponse) => {
      const rawProgress =
         typeof poll.progress === "number"
            ? clampNumber(Math.round(poll.progress), 0, 100)
            : undefined;

      if (rawProgress === undefined) return undefined;

      const previousDisplayed = displayedProgressRef.current;
      if (previousDisplayed === undefined) {
         displayedProgressRef.current = rawProgress;
         return rawProgress;
      }

      const nextDisplayed = clampNumber(rawProgress, previousDisplayed, previousDisplayed + 15);
      displayedProgressRef.current = nextDisplayed;
      return nextDisplayed;
   };

   const stepFromPoll = (poll: BotResponse): ProgressStep => {
      if (poll.status === "queued") return "queued";
      if (poll.status === "failed") return "error";
      if (poll.status === "finished") return "done";

      if (poll.first_results_uploaded) return "selecting";
      return "collecting";
   };

   const titleForStep = (step: ProgressStep) => {
      if (step === "start") return intl.formatMessage({ id: "placefinder.startedSearch" });
      if (step === "queued") return intl.formatMessage({ id: "placefinder.queueWaiting" });
      if (step === "collecting") return intl.formatMessage({ id: "placefinder.collectionData" });
      if (step === "selecting") return intl.formatMessage({ id: "placefinder.chooseBestMatching" });
      if (step === "refreshing") return intl.formatMessage({ id: "placefinder.refreshingTitle" });
      if (step === "error") return intl.formatMessage({ id: "placefinder.failedFinish" });
      return "";
   };

   const subtitleForStep = (step: ProgressStep) => {
      if (step === "start") {
         return radiusLabel
            ? `${intl.formatMessage({ id: "placefinder.radius" })}: ${radiusLabel} • ${intl.formatMessage({ id: "placefinder.startedProcess" })}`
            : intl.formatMessage({ id: "placefinder.startedProcess" });
      }
      if (step === "queued") {
         return intl.formatMessage({ id: "placefinder.orderedAccepted" });
      }
      if (step === "collecting") {
         return intl.formatMessage({ id: "placefinder.searchingPlaces" });
      }
      if (step === "selecting") {
         return intl.formatMessage({ id: "placefinder.selectingPlaces" });
      }
      if (step === "refreshing") {
         return intl.formatMessage({ id: "placefinder.refreshingMayTakeLong" });
      }
      if (step === "error") {
         return intl.formatMessage({ id: "placefinder.error" });
      }
      return "";
   };

   const setCompletedIndexForCurrentStep = (step: ProgressStep) => {
      const indexInFlow = PROGRESS_FLOW.findIndex((item) => item.key === step);
      if (indexInFlow <= 0) {
         completedStepIndexRef.current = -1;
         return;
      }
      completedStepIndexRef.current = indexInFlow - 1;
   };

   const tryFinalize = () => {
      if (!refreshCompletedRef.current) return;
      const pending = pendingFinalMessageRef.current;
      if (!pending) return;

      pendingFinalMessageRef.current = null;

      setChatHistory((prev) => [...prev, pending]);

      setProgressUI({ visible: false, step: "done", title: "" });
      setIsBotResponseLoading(false);
      onJobRunningChange?.(false);
   };

   const startRefreshListAndFinalize = async () => {
      if (refreshStartedRef.current) return;
      refreshStartedRef.current = true;

      setProgressUI((prev) => ({
         ...prev,
         visible: true,
         step: "refreshing",
         title: titleForStep("refreshing"),
         subtitle: subtitleForStep("refreshing"),
         progress: undefined,
         initialResultsToast: false,
      }));
      setCompletedIndexForCurrentStep("refreshing");

      try {
         const res = await refreshList();
         const status = normalizeStatus(res);

         refreshStatusRef.current = status;
         refreshCompletedRef.current = isOkStatus(status);

         if (!refreshCompletedRef.current) {
            setProgressUI({
               visible: true,
               step: "error",
               title: titleForStep("error"),
               subtitle: `${intl.formatMessage({ id: "placefinder.refreshFailed" })} (status: ${status})`,
               progress: undefined,
               initialResultsToast: false,
            });

            setIsBotResponseLoading(false);
            onJobRunningChange?.(false);
            pendingFinalMessageRef.current = null;
            return;
         }

         tryFinalize();
      } catch (e) {
         refreshCompletedRef.current = false;

         setProgressUI({
            visible: true,
            step: "error",
            title: titleForStep("error"),
            subtitle: intl.formatMessage({ id: "placefinder.refreshFailed" }),
            progress: undefined,
            initialResultsToast: false,
         });

         setIsBotResponseLoading(false);
         onJobRunningChange?.(false);
         pendingFinalMessageRef.current = null;
      }
   };

   const handleSend = async () => {
      if (!input.trim()) return;

      setIsBotResponseLoading(true);
      onJobRunningChange?.(true);
      const userMessage = input;
      setInput("");
      setChatHistory((prev) => [
         ...prev,
         { id: createMessageId(), sender: "user", text: userMessage },
      ]);

      // reset per request
      jobCreatedRef.current = false;
      refreshedInitialDataRef.current = false;
      refreshedFinalDataRef.current = false;
      lastPolledStatusRef.current = null;

      displayedProgressRef.current = undefined;
      completedStepIndexRef.current = -1;

      refreshCompletedRef.current = false;
      refreshStatusRef.current = null;
      pendingFinalMessageRef.current = null;
      refreshStartedRef.current = false;

      setProgressUI({
         visible: true,
         step: "start",
         title: titleForStep("start"),
         subtitle: subtitleForStep("start"),
         progress: undefined,
         initialResultsToast: false,
      });

      setCompletedIndexForCurrentStep("start");

      try {
         if (
            !centerLocation ||
            typeof centerLocation.lat !== "number" ||
            typeof centerLocation.lng !== "number"
         ) {
            Message.error(intl.formatMessage({ id: "placefinder.locationRequired" }));
            setProgressUI({ visible: false, step: "done", title: "" });
            setIsBotResponseLoading(false);
            onJobRunningChange?.(false);
            return;
         }

         const location = `${centerLocation.lat},${centerLocation.lng}`;

         const botResponse = await sendRequest(userMessage, location, radius, {
            onPollResponse: (pollResponse: any) => {
               const processKey = pollResponse?.data?.process_key;
               if (processKey && !jobCreatedRef.current) {
                  jobCreatedRef.current = true;

                  setProgressUI((prev) => ({
                     ...prev,
                     visible: true,
                     step: "queued",
                     title: titleForStep("queued"),
                     subtitle: subtitleForStep("queued"),
                     progress: undefined,
                  }));

                  setCompletedIndexForCurrentStep("queued");
                  return;
               }

               const poll = pollResponse?.data as BotResponse | undefined;
               if (!poll?.status) return;

               const previousStatus = lastPolledStatusRef.current;
               lastPolledStatusRef.current = poll.status;

               const nextStep = stepFromPoll(poll);

               if (nextStep === "error") {
                  const errorText = poll.error_message || subtitleForStep("error");
                  setProgressUI({
                     visible: true,
                     step: "error",
                     title: titleForStep("error"),
                     subtitle: errorText,
                     progress: undefined,
                     initialResultsToast: false,
                  });

                  setIsBotResponseLoading(false);
                  onJobRunningChange?.(false);
                  pendingFinalMessageRef.current = null;
                  return;
               }

               // finished -> refreshing
               if (
                  poll.status === "finished" &&
                  previousStatus !== "finished" &&
                  !refreshedFinalDataRef.current
               ) {
                  refreshedFinalDataRef.current = true;
                  startRefreshListAndFinalize();
                  return;
               }

               const progressValue = computeSmoothedProgress(poll);

               setProgressUI((prev) => ({
                  ...prev,
                  visible: poll.status !== "finished",
                  step: nextStep,
                  title: titleForStep(nextStep),
                  subtitle: subtitleForStep(nextStep),
                  progress: progressValue,
               }));

               setCompletedIndexForCurrentStep(nextStep);

               // First results
               if (
                  poll.first_results_uploaded &&
                  poll.status !== "finished" &&
                  !refreshedInitialDataRef.current
               ) {
                  refreshedInitialDataRef.current = true;

                  setProgressUI((prev) => ({
                     ...prev,
                     initialResultsToast: true,
                  }));

                  refreshList();
               }
            },
         });

         if (botResponse.status !== 200) {
            throw new Error("Bot Error");
         }

         const warnings = botResponse.data?.warnings ?? [];
         const hasWarnings = warnings.length > 0;
         const finalText = hasWarnings ? warnings.join(" ") : botResponseMessage;

         // wait for refresh list
         pendingFinalMessageRef.current = {
            id: createMessageId(),
            sender: "bot",
            text: finalText,
            isInitial: false,
            hasWarnings,
            isFinal: !hasWarnings,
            meta: { radiusLabel: radiusLabel || undefined },
         };

         tryFinalize();
      } catch (error) {
         Message.error(intl.formatMessage({ id: "placefinder.botError" }));
         console.error(error);

         const errorText = intl.formatMessage({ id: "placefinder.botError" });
         setProgressUI({
            visible: true,
            step: "error",
            title: titleForStep("error"),
            subtitle: errorText,
            progress: undefined,
            initialResultsToast: false,
         });

         setIsBotResponseLoading(false);
         onJobRunningChange?.(false);
         pendingFinalMessageRef.current = null;
      } finally {
         setTimeout(() => {
            setProgressUI((prev) => ({
               ...prev,
               initialResultsToast: false,
            }));
         }, 2500);
      }
   };

   useEffect(() => {
      localStorage.setItem(CHAT_HISTORY_KEY, JSON.stringify(chatHistory));
   }, [chatHistory]);

   useEffect(() => {
      scrollToBottom();
   }, [chatHistory]);

   useEffect(() => {
      if (isPlaceFinderChatOpen) scrollToBottom();
   }, [isPlaceFinderChatOpen]);

   return (
      <div className="placefinder">
         {isPlaceFinderChatOpen && (
            <div className="placefinder__chat">
               <div className="placefinder__chat-history" ref={chatHistoryRef}>
                  {chatHistory.map((message) => (
                     <div
                        key={message.id}
                        className={clsx(
                           "placefinder__chat-bubble",
                           message.hasWarnings
                              ? "placefinder__chat-bubble--warning"
                              : `placefinder__chat-bubble--${message.sender}`,
                           message.isFinal && "placefinder__chat-bubble--final",
                        )}
                     >
                        {message.isFinal ? (
                           <div className="placefinder__final">
                              <div className="placefinder__final__header">
                                 <span className="placefinder__final__icon">✓</span>
                                 <div className="placefinder__final__title">
                                    {intl.formatMessage({ id: "placefinder.ready" })}
                                 </div>
                                 <div className="placefinder__final__pill">
                                    {intl.formatMessage({ id: "placefinder.listUpdated" })}
                                 </div>
                              </div>

                              <div className="placefinder__final__body">
                                 <div className="placefinder__final__text">
                                    {intl.formatMessage({ id: "placefinder.matched" })}
                                 </div>

                                 {message.meta?.radiusLabel ? (
                                    <div className="placefinder__final__meta">
                                       <div className="placefinder__final__metaItem">
                                          <span className="placefinder__final__metaLabel">
                                             {intl.formatMessage({ id: "placefinder.radius" })}:
                                          </span>
                                          <span className="placefinder__final__metaValue">
                                             {message.meta.radiusLabel}
                                          </span>
                                       </div>
                                    </div>
                                 ) : null}
                              </div>
                           </div>
                        ) : (
                           <span className="placefinder__chat-bubble-text">{message.text}</span>
                        )}
                     </div>
                  ))}
               </div>

               {progressUI.visible && (
                  <div
                     className={`placefinder__progress placefinder__progress--${progressUI.step}`}
                  >
                     <div className="placefinder__progress__top">
                        <div className="placefinder__progress__badge">
                           <span className="placefinder__progress__dot" />
                           <span className="placefinder__progress__badgeText">
                              {STEP_BADGE_LABEL[progressUI.step]}
                           </span>
                        </div>

                        {progressUI.step !== "error" && (
                           <div className="placefinder__progress__spinner" aria-label="loading">
                              <span className="placefinder__progress__spinnerRing" />
                           </div>
                        )}
                     </div>

                     <div className="placefinder__progress__title">{progressUI.title}</div>
                     {progressUI.subtitle ? (
                        <div className="placefinder__progress__subtitle">{progressUI.subtitle}</div>
                     ) : null}

                     {progressUI.step !== "error" && (
                        <>
                           {typeof progressUI.progress === "number" ? (
                              <div className="placefinder__progress__bar">
                                 <div
                                    className="placefinder__progress__barFill"
                                    style={{ width: `${progressUI.progress}%` }}
                                 />
                                 <div className="placefinder__progress__barText">
                                    {progressUI.progress}%
                                 </div>
                              </div>
                           ) : (
                              <div className="placefinder__progress__bar placefinder__progress__bar--indeterminate">
                                 <div className="placefinder__progress__barIndeterminate" />
                              </div>
                           )}
                        </>
                     )}
                  </div>
               )}

               <div className="placefinder__chat-input-row">
                  <input
                     value={input}
                     onChange={(event) => setInput(event.target.value)}
                     onKeyDown={(event) =>
                        event.key === "Enter" && !isBotResponseLoading && handleSend()
                     }
                     placeholder={intl.formatMessage({ id: "placefinder.placeholder" })}
                     className="placefinder__chat-input"
                  />
                  <Button
                     onClick={handleSend}
                     disabled={isBotResponseLoading}
                     className="placefinder__chat-send"
                  >
                     {intl.formatMessage({ id: "modal.sendMail" })}
                  </Button>
               </div>
            </div>
         )}
      </div>
   );
};

export default Placefinder;
